import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Link as LinkIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
  CloudUpload as UploadIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { JobRecord } from '../services/api';

interface JobDetailsModalProps {
  open: boolean;
  onClose: () => void;
  job: JobRecord | null;
}

// Status color mapping
const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',
  uploading: 'primary',
  completed: 'success',
  failed: 'error',
  cancelled: 'default',
} as const;

// Platform colors
const PLATFORM_COLORS = {
  youtube: '#FF0000',
  meta: '#1877F2',
} as const;

// Source display names
const SOURCE_LABELS = {
  slack_dm: 'Slack DM',
  google_drive: 'Google Drive',
  web_interface: 'Web Interface',
} as const;

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ open, onClose, job }) => {
  if (!job) return null;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'processing':
      case 'uploading':
        return <UploadIcon color="primary" />;
      default:
        return <TimeIcon color="action" />;
    }
  };

  const getStatusChip = (status: string) => {
    const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'default';
    return (
      <Chip 
        label={status.charAt(0).toUpperCase() + status.slice(1)} 
        color={color}
        size="small"
        variant="filled"
        icon={getStatusIcon(status)}
      />
    );
  };

  const getPlatformChip = (platform: string) => {
    const color = PLATFORM_COLORS[platform as keyof typeof PLATFORM_COLORS];
    return (
      <Chip 
        label={platform.toUpperCase()} 
        size="small"
        sx={{ 
          backgroundColor: color,
          color: 'white',
          fontWeight: 'bold'
        }}
      />
    );
  };

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getRelativeTime = (timestamp: string) => {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  };

  const renderJsonData = (data: any, title: string) => {
    if (!data || Object.keys(data).length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No {title.toLowerCase()} available
        </Typography>
      );
    }

    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 2, 
          backgroundColor: 'grey.50',
          maxHeight: '300px',
          overflowY: 'auto'
        }}
      >
        <pre style={{ 
          margin: 0, 
          fontSize: '12px', 
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </Paper>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">
              Job Details
            </Typography>
            {getStatusChip(job.status)}
            {getPlatformChip(job.platform)}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          {/* Basic Information */}
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Job ID
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '14px' }}>
                  {job.job_id}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  User ID
                </Typography>
                <Typography variant="body1">
                  {job.user_id}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Channel ID
                </Typography>
                <Typography variant="body1">
                  {job.channel_id}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Source
                </Typography>
                <Typography variant="body1">
                  {job.source ? SOURCE_LABELS[job.source as keyof typeof SOURCE_LABELS] || job.source : 'N/A'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Video Information */}
          <Typography variant="h6" gutterBottom>
            Video Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Video URL
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinkIcon fontSize="small" color="action" />
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      wordBreak: 'break-all',
                      color: 'primary.main',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                    onClick={() => window.open(job.video_url, '_blank')}
                  >
                    {job.video_url}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {job.ratio && (
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Ratio
                  </Typography>
                  <Typography variant="body1">
                    {job.ratio}
                  </Typography>
                </Box>
              </Grid>
            )}
            {job.production_id && (
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Production ID
                  </Typography>
                  <Typography variant="body1">
                    {job.production_id}
                  </Typography>
                </Box>
              </Grid>
            )}
            {job.creative_number && (
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Creative Number
                  </Typography>
                  <Typography variant="body1">
                    {job.creative_number}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Status Information */}
          <Typography variant="h6" gutterBottom>
            Status Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">
                  {formatTime(job.created_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRelativeTime(job.created_at)}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {formatTime(job.updated_at)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {getRelativeTime(job.updated_at)}
                </Typography>
              </Box>
            </Grid>
            {job.last_heartbeat && (
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Last Heartbeat
                  </Typography>
                  <Typography variant="body2">
                    {formatTime(job.last_heartbeat)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getRelativeTime(job.last_heartbeat)}
                  </Typography>
                </Box>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Retry Count
                </Typography>
                <Typography variant="body1">
                  {job.retry_count}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Error Message */}
          {job.error_message && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="h6" gutterBottom color="error">
                  Error Message
                </Typography>
                <Paper 
                  variant="outlined" 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'error.light',
                    color: 'error.contrastText'
                  }}
                >
                  <Typography variant="body2">
                    {job.error_message}
                  </Typography>
                </Paper>
              </Box>
            </>
          )}

          {/* Watchers */}
          {job.watchers && job.watchers.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Watchers
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {job.watchers.map((watcher, index) => (
                    <Chip key={index} label={watcher} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Expandable Sections */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Metadata</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderJsonData(job.metadata, 'Metadata')}
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">Platform Response</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {renderJsonData(job.platform_response, 'Platform Response')}
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default JobDetailsModal;

